import {
    getAction,
    listActions,
} from './queries';
import {
    createAction,
    deleteAction,
    setActionStatus,
    updateAction,
} from './mutations';

import {GraphQLQuery} from '@aws-amplify/api';

import {
    ActionStatus,
    CreateTestingAction,
    DeleteTestingAction,
    GetTestingAction,
    ListTestingActions,
    SetTestingActionStatus,
    TestingAction,
    UpdateTestingAction,
} from '../types.ts';
import {getTestingApiClient} from '../../testing-api-integration/client.ts';

export const fetchRecords = async (filter: object) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<ListTestingActions>>({
        query: listActions,
        variables: {filter: filter},
    });
    return response.data?.listActions.items ?? [];
};

export const fetch = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<GetTestingAction>>({
        query: getAction,
        variables: {id: id},
    });
    return response.data?.getAction;
};

export const add = async (payload: TestingAction) => {
    // Whitelist the CreateActionInput fields — never pass the whole record through.
    // `status` is NonNull with no server default: omitting it fails with
    // "Variable 'input' has coerced Null value for NonNull type 'ActionStatus!'".
    const input = {
        actionCode: payload.actionCode,
        name: payload.name,
        // `applicationId: ID` is NULLABLE (FNP-976). An empty form value is sent
        // as null rather than '' — an empty string is a real (dangling) id as
        // far as the store is concerned, whereas null means "no application".
        applicationId: payload.applicationId || null,
        status: payload.status,
    };

    const response = await getTestingApiClient().graphql<GraphQLQuery<CreateTestingAction>>({
        query: createAction,
        variables: {input: input},
    });
    return response.data?.createAction;
};

export const update = async (id: string, record: TestingAction) => {
    // Every field is sent explicitly — updateAction SETs them unconditionally,
    // so a partial payload would blank the omitted ones. (`applicationId` is the
    // one nullable member of UpdateActionInput since FNP-976; the rest are
    // NonNull.) `status` is carried through unchanged — the edit form does not
    // expose it; transitions go through setStatus() below.
    const input = {
        id: id,
        actionCode: record.actionCode,
        name: record.name,
        applicationId: record.applicationId || null,
        status: record.status,
    };

    const response = await getTestingApiClient().graphql<GraphQLQuery<UpdateTestingAction>>({
        query: updateAction,
        variables: {input: input},
    });
    return response.data?.updateAction;
};

export const deleteRecord = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<DeleteTestingAction>>({
        query: deleteAction,
        variables: {input: {id: id}},
    });
    return response.data?.deleteAction;
};

export const setStatus = async (id: string, targetStatus: ActionStatus) => {
    // Direct args, NOT an input object.
    const response = await getTestingApiClient().graphql<GraphQLQuery<SetTestingActionStatus>>({
        query: setActionStatus,
        variables: {
            id: id,
            targetStatus: targetStatus,
        },
    });
    return response.data?.setActionStatus;
};
